/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { useContext, useEffect, useState } from 'react'
import useMediaQuery from 'beautiful-react-hooks/useMediaQuery'
import {
    Container,
    Dropdown,
    Image,
    Menu,
    Icon, DropdownItem,
} from 'semantic-ui-react'
import { UltyModalWrapperContext } from '../Shared/UltyModalWrapper/UltyModalWrapperContext'
import ProviderChoice from '../Auth/ProviderChoice'
import { Link } from 'react-router-dom-v5-compat'
import Avatar from 'react-avatar'
import 'tippy.js/dist/tippy.css'
import SyncDot from './SyncDot'
import OrderCount from './OrderCount'
import SyncIcon from './SyncIcon'
import { useTranslation } from 'react-i18next'
import { useSocket } from '../../services/socket'
import useUser from '../Shared/UserProvider/useUser'
import { AUTH_TOKEN, MOBILE_THRESHOLD, TABLET_THRESHOLD } from '../../constants'
import { isProviderACompany, isProviderAPos } from '../../services/Provider/Provider.service'
import { useQueryClient } from '@tanstack/react-query'
import useCanAccessHelp from '../Help/hooks/useCanAccessHelp'
import { useLocalStorageItem } from '../../helpers/useLocalStorageItem'
import useFeature from '../Shared/FeatureProvider/useFeature'

export const NAVBAR_HEIGHT_IN_PX = 60

const NavBar = ({history, location}) => {
    const socket = useSocket()
    const {user, setUser, can, landingRoute} = useUser()
    const [dashboardSoftRolloutTarget] = useLocalStorageItem('dashboard_soft_rollout_target')
    const [t] = useTranslation()
    const isMobileDevice = useMediaQuery(`(max-width: ${MOBILE_THRESHOLD}px)`)
    const isTabletDevice = useMediaQuery(`(max-width: ${TABLET_THRESHOLD}px)`)
    const [hideSecondaryMenu, setHideSecondaryMenu] = useState(true)
    const {handleUltyModalWrapper} = useContext(UltyModalWrapperContext)
    const queryClient = useQueryClient()
    const {isEnabled} = useFeature()

    const isAndroidDevice = () => {
        return (window.Android && window.Android.getToken) || window.webkit?.messageHandlers
    }

    const canAccessHelp = useCanAccessHelp()
    const shouldDisplayAdminMenu = can('READ', 'settings') || isAndroidDevice()

    const defaultSettingsRoute = isAndroidDevice() ?
      '/settings/device' :
      user.isLoggedInOwnScope ?
        '/settings/platforms' :
        shouldDisplayAdminMenu ?
          `/settings/reports` :
          ''

    useEffect(() => {
        setHideSecondaryMenu(isMobileDevice || (user.isAdmin() && isTabletDevice))
    }, [user, isMobileDevice, isTabletDevice])

    const defineProviderChoiceModal = (providers, email, loginContext) => {
        const modalSettings = {
            centered: false,
            title: t('login.provider_choice'),
            component: <ProviderChoice
              providers={providers}
              email={email}
              loginContext={loginContext}
              afterSubmit={data => {
                  queryClient.clear()
                  localStorage.setItem(AUTH_TOKEN, data.token)
                  setUser(data.user)

                  history.push('/')

                  handleUltyModalWrapper(false, null)
              }}
            />,
        }

        handleUltyModalWrapper(true, modalSettings)
    }

    return (
      <Menu
        fixed="top"
        inverted
        css={css`
            height: ${NAVBAR_HEIGHT_IN_PX}px;
            z-index: 1000 !important;
            ${isMobileDevice && `
                &.ui.menu .item {
                    padding-left: 10px;
                    padding-right: 10px;
                }
            `}
            ${hideSecondaryMenu && `
                &.ui.menu .item > i.dropdown.icon {
                    margin: .2em;
                }
            `}
        `}>
          <Container>
              {!isMobileDevice && (<Menu.Item as={Link} to={landingRoute(null, dashboardSoftRolloutTarget)} header>
                  <Image size="mini" src="/img/logo.png" style={{marginRight: '1em'}}/>
              </Menu.Item>)}
              {isEnabled('dashboard') && <Menu.Item
                as={Link}
                to={can('READ', 'metric') ?
                  '/dashboard' :
                  landingRoute(null, dashboardSoftRolloutTarget)}
              >
                  <Icon
                    name={'home'}
                    fitted
                    css={css`
                        &.icon {
                            color: white;
                        }
                    `}
                  />
              </Menu.Item>}
              {can('READ', 'metric') &&
                <Menu.Item
                  as={Link}
                  to={location.pathname.indexOf('/analytics') === 0 ? `${location.pathname}${location.search}` : '/analytics'}
                  active={location.pathname.indexOf('/analytics') === 0}>
                    {t('navbar.home')}
                </Menu.Item>}
              {can('READ', 'order') && <Menu.Item
                as={Link}
                to={user?.provider?.type === 'COMPANY' ? '/orders/finished' : '/orders/current'}
                active={location.pathname.indexOf('/orders') === 0}>
                  {t('navbar.orders')}
              </Menu.Item>}
              {!hideSecondaryMenu && can('STATIC', 'categories') && <Menu.Item as={Link} to="/categories"
                                                                               active={location.pathname.indexOf('/categories') === 0}>{t('navbar.categories')}</Menu.Item>}
              {(!isMobileDevice || !can('READ', 'metric')) && can('READ', 'product') &&
                <Menu.Item
                  as={Link}
                  to={{
                      pathname: `/products`,
                      search: isProviderAPos(user.provider) ? new URLSearchParams({
                          unavailableUntil: false,
                      }).toString() : undefined,
                  }}
                  active={location.pathname.indexOf('/products') === 0}>
                    {t('navbar.products')}
                </Menu.Item>
              }
              {(!hideSecondaryMenu && shouldDisplayAdminMenu) && (
                <Menu.Item
                  as={Link}
                  to={defaultSettingsRoute}
                  active={location.pathname.indexOf(defaultSettingsRoute) === 0}>
                    {t('navbar.admin')}
                </Menu.Item>
              )}

              <Menu.Menu position="right">
                  {(isProviderACompany(user.provider) || user.isLoggedInOwnScope) ? (
                    <SyncIcon/>
                  ) : (
                    <>
                        <OrderCount/>
                        <SyncIcon/>
                        <SyncDot/>
                    </>
                  )}

                  <Dropdown item trigger={
                      !hideSecondaryMenu ? (
                        <span>
                <Avatar name={`${user.firstname} ${user.lastname}`} round size={28} textSizeRatio={1}
                        style={{width: '28px', height: '28px'}}/> {`${user.firstname || ''} ${user.lastname || ''}`}
              </span>
                      ) : (
                        <Icon name="bars"/>
                      )
                  } className="navbar-dropdown">
                      <Dropdown.Menu>
                          {hideSecondaryMenu && can('STATIC', 'categories') && (
                            <Dropdown.Item as={Link} to="/categories">{t('navbar.categories')}</Dropdown.Item>
                          )}
                          {(isMobileDevice && can('READ', 'metric')) && can('READ', 'product') && (
                            <Dropdown.Item
                              as={Link}
                              to={{
                                  pathname: `/products`,
                                  search: isProviderAPos(user.provider) ? new URLSearchParams({
                                      unavailableUntil: false,
                                  }).toString() : undefined,
                              }}>
                                {t('navbar.products')}
                            </Dropdown.Item>

                          )}
                          {(hideSecondaryMenu && shouldDisplayAdminMenu) && (
                            <Dropdown.Item as={Link} to={defaultSettingsRoute}>{t('navbar.admin')}</Dropdown.Item>
                          )}
                          {(user?.providers || []).length > 1 && (
                            <Dropdown.Item onClick={() => {
                                defineProviderChoiceModal(user.providers, user.email, user.loginContext)
                            }}>{t('navbar.change_account')}</Dropdown.Item>
                          )}
                          <DropdownItem as={Link} to={`/administration/account`}>
                              {t('administration.submenus.myAccount')}
                          </DropdownItem>
                          {canAccessHelp && (
                            <Dropdown.Item as={Link} to="/help" target="_blank">
                                {t('navbar.helpcenter')}&nbsp;&nbsp;
                                <Icon name="external alternate" size="tiny" css={css`font-size: 0.5em !important`}/>
                            </Dropdown.Item>

                          )}
                          <Dropdown.Item onClick={async () => {
                              await socket.disconnect()
                              history.push('/auth/login')
                          }}>{t('navbar.disconnect')}</Dropdown.Item>
                      </Dropdown.Menu>
                  </Dropdown>
              </Menu.Menu>
          </Container>
      </Menu>
    )
}

export default NavBar
